import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BadgeCheckIcon, BookOpenIcon, CalculatorIcon, FlaskConicalIcon, HouseIcon, ShoppingBagIcon, XIcon } from "lucide-react";
export type VerifiedShopifyPurchase = {
  firstName: string;
  lastName: string;
  location: string;
  purchasedAt: string;
};
type BenefitNotificationProps = {
  verifiedPurchases?: VerifiedShopifyPurchase[];
};
const benefitNotifications = [{
  message: "Aprendé a fabricar productos de limpieza desde casa",
  icon: FlaskConicalIcon
}, {
  message: "Acceso digital inmediato a Fórmula Emprendedora",
  icon: BookOpenIcon
}, {
  message: "Incluye herramientas para calcular costos y precios",
  icon: CalculatorIcon
}, {
  message: "Aprendé a fabricar para vos o para emprender",
  icon: HouseIcon
}];
export function BenefitNotification({
  verifiedPurchases = []
}: BenefitNotificationProps) {
  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const hasVerifiedPurchases = verifiedPurchases.length > 0;
  const notificationsCount = hasVerifiedPurchases ? verifiedPurchases.length : benefitNotifications.length;
  useEffect(() => {
    let index = 0;
    let dismissTimer: ReturnType<typeof setTimeout> | undefined;
    const showNotification = () => {
      setActiveIndex(index);
      index = (index + 1) % notificationsCount;
      dismissTimer = setTimeout(() => setActiveIndex(null), 5000);
    };
    const interval = setInterval(showNotification, 20000);
    const initialTimer = setTimeout(showNotification, 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(initialTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [notificationsCount]);
  const verifiedPurchase = activeIndex === null || !hasVerifiedPurchases ? null : verifiedPurchases[activeIndex];
  const benefitNotification = activeIndex === null || hasVerifiedPurchases ? null : benefitNotifications[activeIndex];
  const BenefitIcon = benefitNotification?.icon;
  const notificationKey = verifiedPurchase ? `${verifiedPurchase.firstName}-${verifiedPurchase.purchasedAt}` : benefitNotification?.message;
  return <div className="pointer-events-none fixed bottom-24 left-4 z-40 w-[min(320px,calc(100vw-2rem))] sm:bottom-5 sm:left-5" aria-live="polite" data-id="element-1" data-mp="12st34ytiy73b">
      <AnimatePresence data-id="element-2" data-mp="1ujfmw2z8togo">
        {activeIndex !== null && <motion.div key={notificationKey} className="pointer-events-auto flex items-start gap-3 rounded-2xl bg-white p-4 shadow-[0_12px_32px_rgba(9,26,55,0.15)]" initial={reduceMotion ? false : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-3" data-mp="1uinsuwlanysi">
            {verifiedPurchase ? <VerifiedPurchaseContent purchase={verifiedPurchase} data-id="element-4" data-mp="29fokz17edvri" /> : BenefitIcon && <BenefitContent Icon={BenefitIcon} message={benefitNotification?.message ?? ""} data-id="element-5" data-mp="2gs4bqg1ty664" />}
            <button type="button" onClick={() => setActiveIndex(null)} className="-mr-1 -mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#657189] transition-colors duration-150 hover:bg-[#f1f5f9] hover:text-[#091a37] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0077e8]" aria-label="Cerrar notificación" data-id="element-6" data-mp="2lqpccgkzurv3">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-7" data-mp="34o8ldk7gcahi" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VerifiedPurchaseContent({
  purchase
}: {
  purchase: VerifiedShopifyPurchase;
}) {
  return <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e2f7ed] text-[#07966b]" data-id="element-8" data-mp="3ihzn46exyq2o">
        <ShoppingBagIcon className="h-4 w-4" aria-hidden="true" data-id="element-9" data-mp="3nhci5efyv3gc" />
      </span>
      <div className="min-w-0 pt-0.5" data-id="element-10" data-mp="39t2jsk1cw90d">
        <p className="text-sm font-extrabold leading-5 text-[#263754]" data-id="element-11" data-mp="39s11bui0gk8m">{formatBuyerName(purchase)} · {purchase.location}</p>
        <p className="mt-0.5 text-sm font-semibold leading-5 text-[#263754]" data-id="element-12" data-mp="35q63izzagozl">Ya accedió a Fórmula Emprendedora</p>
        <p className="mt-1 flex items-center gap-1 text-xs text-[#657189]" data-id="element-13" data-mp="3ekatziaifyel"><BadgeCheckIcon className="h-3.5 w-3.5 text-[#07966b]" aria-hidden="true" data-id="element-14" data-mp="3el2kwft5x7yj" />Compra verificada · {formatElapsedTime(purchase.purchasedAt)}</p>
      </div>
    </>;
}
function BenefitContent({
  Icon,
  message
}: {
  Icon: typeof FlaskConicalIcon;
  message: string;
}) {
  return <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e9f6ff] text-[#0077e8]" data-id="element-15" data-mp="30ugywi8ewhwf">
        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-16" data-mp="2vvw1eqkjocbo" />
      </span>
      <p className="pt-1 text-sm font-semibold leading-5 text-[#263754]" data-id="element-17" data-mp="g1dj91ep0uye">{message}</p>
    </>;
}
function formatBuyerName({
  firstName,
  lastName
}: VerifiedShopifyPurchase) {
  const initial = lastName.trim().charAt(0);
  return initial ? `${firstName.trim()} ${initial}.` : firstName.trim();
}
function formatElapsedTime(purchasedAt: string) {
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(purchasedAt).getTime()) / 60000));
  if (elapsedMinutes < 60) return `hace ${Math.max(1, elapsedMinutes)} min`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `hace ${elapsedHours} h`;
  return `hace ${Math.floor(elapsedHours / 24)} días`;
}